import React, { useCallback, useEffect, useRef, useState } from 'react';
import TermsContent from '../components/TermsContent';
import { supabase, exchangeCodeIfPresent } from '../lib/supabase';
import { authApi } from '../services/api';

const TERMS_STORAGE_KEY = 'loconvo_terms_v1';
const TERMS_VERSION = 'v1';
const TERMS_ENFORCEMENT_CUTOFF_MS = Date.parse(
  process.env.REACT_APP_TERMS_ENFORCEMENT_CUTOFF || '2026-02-16T00:00:00.000Z'
);

const shouldEnforceTermsForAccount = (createdAt?: string | null) => {
  if (!Number.isFinite(TERMS_ENFORCEMENT_CUTOFF_MS)) return true;
  const createdAtMs = Date.parse(createdAt || '');
  // Avoid dead-end auth loops for malformed legacy rows.
  if (!Number.isFinite(createdAtMs)) return false;
  return createdAtMs >= TERMS_ENFORCEMENT_CUTOFF_MS;
};

const readLocalTermsAccepted = () => {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(TERMS_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const writeLocalTermsAccepted = () => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(TERMS_STORAGE_KEY, 'true');
  } catch {
    // ignore storage errors
  }
};

const AuthPage: React.FC = () => {
  const [signedIn, setSignedIn] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<'error' | 'success' | 'info'>('info');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsScrolled, setTermsScrolled] = useState(false);
  const [pendingGoogle, setPendingGoogle] = useState(false);
  const [termsModalForSession, setTermsModalForSession] = useState(false);
  const termsRef = useRef<HTMLDivElement | null>(null);
  const [termsAccepted, setTermsAccepted] = useState<boolean>(() => readLocalTermsAccepted());

  useEffect(() => {
    if (termsAccepted && typeof window !== 'undefined') {
      writeLocalTermsAccepted();
    }
  }, [termsAccepted]);

  const handleTermsScroll = () => {
    const el = termsRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
    if (atBottom) setTermsScrolled(true);
  };

  const maybeRedirect = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const to = params.get('redirect') || '/';
    // Small delay so tokens persist
    setTimeout(() => { window.location.replace(to); }, 200);
  }, []);

  const handleSessionRedirect = useCallback(async (session: any) => {
    // Attempt to ensure username is set from Supabase metadata for email/password signups
    const desiredUsername = (session.user?.user_metadata as any)?.username || (session.user?.user_metadata as any)?.user_name;

    console.log('🔍 handleSessionRedirect - Session user:', session.user?.id, session.user?.email);
    console.log('🔍 handleSessionRedirect - Desired username:', desiredUsername);

    // Check if user has username before redirecting
    try {
      console.log('🔍 Calling getCurrentUser...');
      const user = await authApi.getCurrentUser();
      console.log('✅ getCurrentUser response:', user);

      // Only enforce terms if backend explicitly returns terms fields.
      // This prevents login dead-ends during partial deployments.
      const hasTermsSignal =
        Object.prototype.hasOwnProperty.call(user, 'termsAcceptedAt')
        || Object.prototype.hasOwnProperty.call(user, 'termsVersion');
      const shouldEnforceTerms = shouldEnforceTermsForAccount((user as any)?.createdAt);
      if (hasTermsSignal && shouldEnforceTerms && !user.termsAcceptedAt) {
        setTermsAccepted(false);
        setTermsModalForSession(true);
        setPendingGoogle(false);
        setTermsScrolled(false);
        setShowTermsModal(true);
        setMessage('Please review and accept the Terms to continue.');
        setMessageType('info');
        return;
      }

      if (hasTermsSignal && (user.termsAcceptedAt || !shouldEnforceTerms)) {
        setTermsAccepted(true);
        writeLocalTermsAccepted();
      }

      if (!user.username && desiredUsername) {
        try {
          await authApi.setUsername(desiredUsername);
          maybeRedirect();
          return;
        } catch (err) {
          // fall through to setup
        }
      }
      if (!user.username) {
        // New user without username - go to setup page
        window.location.href = '/setup-username';
        return;
      }
      // User has username - redirect normally
      maybeRedirect();
    } catch (err: any) {
      const status = err?.response?.status;
      const code = err?.response?.data?.code;
      const errorMsg = err?.response?.data?.error;

      console.error('❌ getCurrentUser error:', {
        status,
        code,
        errorMsg,
        fullError: err?.response?.data
      });

      if (status === 404 && (code === 'PROFILE_NOT_FOUND' || !code)) {
        console.log('➡️ Redirecting to /setup-username (new user)');
        window.location.href = '/setup-username';
        return;
      }
      // If user is not found (401/410) or deleted, sign them out
      if (status === 503 && code === 'AUTH_PROVIDER_UNVERIFIED') {
        console.error('⚠️ Auth provider unverified');
        setMessage('Unable to validate your sign-in. Please try again.');
        setMessageType('error');
        return;
      }
      if (status === 410 || code === 'SUPABASE_ACCOUNT_DELETED') {
        console.error('🚫 Account deleted from Supabase:', { status, code, errorMsg });
        if (supabase) {
          try { await supabase.auth.signOut(); } catch { /* ignore */ }
        }
        setSignedIn(false);
        setMessage(errorMsg || 'Your account was deleted and cannot be recreated. Please try signing up again or contact support.');
        setMessageType('error');
        return;
      }
      if (status === 403 && code === 'RECREATE_COOLDOWN') {
        if (supabase) {
          try { await supabase.auth.signOut(); } catch { /* ignore */ }
        }
        setSignedIn(false);
        setMessage('This account was recently deleted. Please wait before re-creating.');
        setMessageType('error');
        return;
      }
      if (status === 403 && typeof errorMsg === 'string' && errorMsg.toLowerCase().includes('banned')) {
        if (supabase) {
          try { await supabase.auth.signOut(); } catch { /* ignore */ }
        }
        setSignedIn(false);
        setMessage(errorMsg || 'Your account is banned. Please contact support.');
        setMessageType('error');
        return;
      }
      if (status === 401) {
        console.error('🚫 Unauthorized:', { status, code, errorMsg });
        if (supabase) {
          try { await supabase.auth.signOut(); } catch { /* ignore */ }
        }
        setSignedIn(false);
        setMessage('Session expired. Please sign in again.');
        setMessageType('error');
        return;
      }
      // For other errors, try to set username from metadata for new legitimate users
      if (desiredUsername) {
        try {
          await authApi.setUsername(desiredUsername);
          maybeRedirect();
          return;
        } catch (e: any) {
          // If setUsername also fails with auth errors, sign out
          const setUsernameStatus = e?.response?.status;
          const setUsernameCode = e?.response?.data?.code;
          if (setUsernameStatus === 401 || setUsernameStatus === 404 || setUsernameStatus === 410 || setUsernameCode === 'SUPABASE_ACCOUNT_DELETED') {
            if (supabase) {
              try { await supabase.auth.signOut(); } catch { /* ignore */ }
            }
            setSignedIn(false);
            return;
          }
          // continue to setup for other errors
        }
      }
      window.location.href = '/setup-username';
    }
  }, [maybeRedirect]);

  useEffect(() => {
    // PKCE: exchange code in URL for a session on initial load
    exchangeCodeIfPresent();

    if (!supabase) return;
    supabase.auth.getSession().then(async ({ data }) => {
      setSignedIn(!!data.session);
      if (data.session) {
        // Check if user has username before redirecting
        await handleSessionRedirect(data.session);
      }
    });
    const { data: sub } = supabase.auth.onAuthStateChange(async (_e, session) => {
      setSignedIn(!!session);
      if (session) {
        await handleSessionRedirect(session);
      }
    });
    return () => { sub?.subscription?.unsubscribe(); };
  }, [handleSessionRedirect]);

  if (!supabase) {
    return (
      <div style={{ maxWidth: 420, margin: '40px auto', fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif' }}>
        <h2>Sign In</h2>
        <p>Supabase is not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY, then redeploy.</p>
      </div>
    );
  }

  const SITE_URL = (process.env.REACT_APP_SITE_URL || window.location.origin).replace(/\/$/, '');
  const redirectTo = SITE_URL + '/login';

  const persistTermsAcceptance = async (): Promise<boolean> => {
    writeLocalTermsAccepted();
    setTermsAccepted(true);
    if (!supabase) return true;
    try {
      const { data } = await supabase.auth.getSession();
      if (!data.session) return true;
      await authApi.acceptTerms(TERMS_VERSION);
      return true;
    } catch (err: any) {
      const status = err?.response?.status;
      const code = err?.response?.data?.code;
      if (status === 404 && (code === 'PROFILE_NOT_FOUND' || !code)) {
        // No local profile yet (new OAuth user before username setup) — local cache is enough for now.
        return true;
      }
      setMessage('Failed to save Terms acceptance. Please try again.');
      setMessageType('error');
      return false;
    }
  };

  const closeTermsModal = async () => {
    const wasSessionEnforcement = termsModalForSession;
    setShowTermsModal(false);
    setPendingGoogle(false);
    setTermsModalForSession(false);
    if (wasSessionEnforcement) {
      if (supabase) {
        try { await supabase.auth.signOut(); } catch { /* ignore */ }
      }
      setSignedIn(false);
      setMessage('You need to accept the Terms to continue.');
      setMessageType('info');
    }
  };

  const signInWithGoogle = async (skipTermsCheck = false) => {
    if (!skipTermsCheck && !termsAccepted) {
      setPendingGoogle(true);
      setTermsModalForSession(false);
      setShowTermsModal(true);
      setTermsScrolled(false);
      return;
    }
    try {
      if (!supabase) {
        setMessage('Authentication is not configured.');
        setMessageType('error');
        return;
      }
      // Redirect back to /login after Google OAuth completes
      // The auth state change listener will handle the username setup redirect
      await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: SITE_URL + '/login'
        }
      });
    } catch (e) {
      setMessage('Failed to start Google sign-in');
      setMessageType('error');
    }
  };

  const sendMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!email.trim()) {
      setMessage('Please enter your email address to receive a sign‑in link');
      setMessageType('error');
      return;
    }

    setSending(true);
    try {
      if (!supabase) {
        setMessage('Authentication is not configured.');
        setMessageType('error');
        return;
      }
      const { error } = await supabase.auth.signInWithOtp({ email: email.trim(), options: { emailRedirectTo: redirectTo } });
      if (error) throw error;
      setMessage('Check your inbox for a sign‑in link. It may take up to a minute — also check spam/junk.');
      setMessageType('success');
    } catch (err: any) {
      setMessage(err?.message || 'Failed to send magic link');
      setMessageType('error');
    } finally {
      setSending(false);
    }
  };

  const signInWithPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!email.trim()) {
      setMessage('Please enter your email address');
      setMessageType('error');
      return;
    }

    if (!password) {
      setMessage('Please enter your password');
      setMessageType('error');
      return;
    }

    setSending(true);
    try {
      if (!supabase) {
        setMessage('Authentication is not configured.');
        setMessageType('error');
        return;
      }
      const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
      if (error) throw error;
      setMessage('Signed in. Redirecting…');
      setMessageType('success');
      // Session listener will redirect shortly
    } catch (err: any) {
      setMessage(err?.message || 'Failed to sign in');
      setMessageType('error');
    } finally {
      setSending(false);
    }
  };

  const requestPasswordReset = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setMessage('Enter your email above first.');
      setMessageType('error');
      return;
    }
    setSending(true);
    setMessage(null);
    try {
      if (!supabase) {
        setMessage('Authentication is not configured.');
        setMessageType('error');
        return;
      }
      const SITE_URL = (process.env.REACT_APP_SITE_URL || window.location.origin).replace(/\/$/, '');
      const resetRedirectTo = SITE_URL + '/reset-password';
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo: resetRedirectTo });
      if (error) throw error;
      setMessage('Password reset email sent. Check your inbox.');
      setMessageType('success');
    } catch (err: any) {
      setMessage(err?.message || 'Failed to send reset email');
      setMessageType('error');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
    <div className="auth-wrapper" style={{ fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif' }}>
      <div className="auth-card">
        {/* Header Section - Clear visual hierarchy */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <h1 className="auth-title">Sign in / Create Account</h1>
          <p className="auth-subtitle">Use email link or password.</p>
        </div>

        {/* Primary CTA - Most prominent action */}
        <div style={{ marginBottom: 24 }}>
          <a href="/register" className="btn btn-primary btn-compact" style={{ display: 'inline-block', width: '100%', textAlign: 'center', fontWeight: 600, fontSize: 16, padding: '12px 16px', boxSizing: 'border-box' }}>
            New here? Create an account
          </a>
        </div>

        {/* Form Section - Grouped inputs */}
        <div style={{ marginBottom: 24 }}>
          {message && (
            <div style={{
              background: messageType === 'error' ? '#FEE2E2' : messageType === 'success' ? '#DCFCE7' : '#F3F4F6',
              border: messageType === 'error' ? '1px solid #EF4444' : messageType === 'success' ? '1px solid #22C55E' : '1px solid #E5E7EB',
              color: messageType === 'error' ? '#991B1B' : messageType === 'success' ? '#166534' : '#374151',
              padding: '12px 16px',
              borderRadius: 8,
              marginBottom: 20,
              fontSize: 14,
              fontWeight: messageType === 'error' ? 600 : 500
            }}>
              {message}
            </div>
          )}

          <div style={{ display: 'grid', gap: 16 }}>
            <div style={{ display: 'grid', gap: 8 }}>
              <label htmlFor="email" style={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>Email</label>
              <input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 16,
                  transition: 'border-color 0.2s ease',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                required
              />
            </div>
            <div style={{ display: 'grid', gap: 8 }}>
              <label htmlFor="password" style={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>Password</label>
              <input
                id="password"
                type="password"
                placeholder="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: 8,
                  fontSize: 16,
                  transition: 'border-color 0.2s ease',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                required
              />
            </div>
          </div>
        </div>

        {/* Action Buttons - Clear visual hierarchy */}
        <div style={{ display: 'grid', gap: 12, marginBottom: 16 }}>
          <form onSubmit={signInWithPassword}>
            <button
              type="submit"
              disabled={sending}
              className="btn btn-primary"
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: 16,
                fontWeight: 600,
                boxSizing: 'border-box'
              }}
            >
              {sending ? 'Signing in…' : 'Sign in with password'}
            </button>
          </form>
          <form onSubmit={sendMagicLink}>
            <button
              type="submit"
              disabled={sending}
              className="btn btn-outline"
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: 14,
                boxSizing: 'border-box'
              }}
            >
              {sending ? 'Sending…' : 'Send sign-in link'}
            </button>
          </form>
          <div style={{ color: '#6b7280', fontSize: 12, lineHeight: 1.4 }}>
            We’ll email you a secure sign‑in link. It can take up to a minute — also check your spam/junk folder.
          </div>
        </div>

        {/* Helper Link - Subtle but accessible */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <button
            type="button"
            onClick={requestPasswordReset}
            style={{
              background: 'none',
              border: 'none',
              color: '#6b7280',
              fontSize: 14,
              cursor: 'pointer'
            }}
            onMouseOver={(e) => (e.target as HTMLElement).style.textDecoration = 'underline'}
            onMouseOut={(e) => (e.target as HTMLElement).style.textDecoration = 'none'}
          >
            Forgot password?
          </button>
        </div>

        {/* Divider - Visual separation */}
        <div className="auth-divider" style={{ margin: '24px 0' }}>or</div>

        {/* Alternative Sign In - Secondary action */}
        <button
          type="button"
          onClick={() => signInWithGoogle()}
          className="btn btn-outline"
          style={{
            width: '100%',
            padding: '12px 16px',
            fontSize: 15,
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            boxSizing: 'border-box'
          }}
        >
          <svg aria-hidden="true" width="20" height="20" viewBox="0 0 48 48">
            <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303C33.876 33.91 29.345 37 24 37c-7.18 0-13-5.82-13-13s5.82-13 13-13c3.313 0 6.315 1.237 8.598 3.263l5.657-5.657C34.91 5.092 29.73 3 24 3 12.402 3 3 12.402 3 24s9.402 21 21 21 21-9.402 21-21c0-1.4-.144-2.764-.389-3.917z" />
            <path fill="#FF3D00" d="M6.306 14.691l6.571 4.814C14.655 16.108 18.938 13 24 13c3.313 0 6.315 1.237 8.598 3.263l5.657-5.657C34.91 5.092 29.73 3 24 3 16.318 3 9.656 7.337 6.306 14.691z" />
            <path fill="#4CAF50" d="M24 45c5.258 0 10.067-2.015 13.682-5.318l-6.315-5.338C29.145 35.463 26.68 36.5 24 36.5 18.676 36.5 14.16 33.437 12.71 29.114l-6.49 5.005C9.513 39.973 16.227 45 24 45z" />
            <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-1.35 3.91-5.045 6.5-9.303 6.5-3.544 0-6.676-1.806-8.487-4.528l-6.49 5.005C13.06 39.473 18.227 42.5 24 42.5c9.39 0 17-7.61 17-17 0-1.4-.144-2.764-.389-3.917z" />
          </svg>
          Continue with Google
        </button>

        {signedIn && <p style={{ textAlign: 'center', marginTop: 16, color: '#059669', fontWeight: 500 }}>Signed in. Redirecting…</p>}
      </div>
    </div>
    {showTermsModal && (
      <div
        className="modal-backdrop"
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: 12
        }}
        role="dialog"
        aria-modal="true"
      >
        <div
          className="modal-card"
          style={{
            background: 'white',
            borderRadius: 12,
            maxWidth: 640,
            width: '100%',
            maxHeight: '90vh',
            overflow: 'hidden',
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb' }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Terms &amp; Privacy</h3>
            <p style={{ margin: '6px 0 0', color: '#4b5563', fontSize: 14 }}>
              {termsModalForSession
                ? 'Please review and accept to continue with this account.'
                : 'Please review before continuing with Google sign-in.'}
            </p>
          </div>
          <TermsContent
            ref={termsRef}
            onScroll={handleTermsScroll}
            style={{
              padding: '16px 20px',
              maxHeight: '60vh',
              overflowY: 'auto',
              fontSize: 14,
              color: '#374151',
              lineHeight: 1.55,
              WebkitOverflowScrolling: 'touch',
              flex: 1,
              minHeight: 240
            }}
          />
          <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button
              className="btn btn-ghost"
              onClick={() => { closeTermsModal(); }}
              style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: 'white', cursor: 'pointer' }}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              disabled={!termsScrolled}
              onClick={async () => {
                const accepted = await persistTermsAcceptance();
                if (!accepted) return;

                setShowTermsModal(false);
                const shouldContinue = pendingGoogle;
                setPendingGoogle(false);
                const shouldResumeSession = termsModalForSession;
                setTermsModalForSession(false);
                if (shouldContinue) {
                  setTimeout(() => { signInWithGoogle(true); }, 50);
                  return;
                }

                if (shouldResumeSession && supabase) {
                  const { data } = await supabase.auth.getSession();
                  if (data.session) {
                    await handleSessionRedirect(data.session);
                  }
                }
              }}
              style={{
                padding: '10px 16px',
                borderRadius: 8,
                border: 'none',
                background: termsScrolled ? '#2563eb' : '#93c5fd',
                color: 'white',
                fontWeight: 700,
                cursor: termsScrolled ? 'pointer' : 'not-allowed'
              }}
            >
              {termsScrolled ? 'I Agree' : 'Scroll to Agree'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default AuthPage;
